<template>
  <app-content class="network-report-view">
    <paged-report-panel
      v-model="pagedFilter"
      :total-rows="totalItems"
      :loading="loading"
      :show-search-term="true"
      :search-placeholder="$t('reports.network-status.search')"
      :search-term-property="'assetSearchTerm'"
      @input="onRefreshSearch"
    >
      <s-table
        :busy="loading"
        :fields="fields"
        :items="history"
        :empty-text="$t('general.empty-list')"
      >
        <template #head(dateTime)="{ label }">
          <div class="text-center">{{ label }}</div>
        </template>
        <template #cell(dateTime)="data">
          <div class="text-center">{{
              data.item.dateTime | dateTimeFilter({
                format: formats.DATETIME_SHORT_WITH_SECONDS
              })
            }}</div>
        </template>
        <template #cell(networkStatus)="data">
          <network-status-badge
            :value="data.item.networkStatus"
          />
        </template>
        <template #cell(moduleStatus)="data">
          <module-status-badge
            :value="data.item.moduleStatus"
          />
        </template>
        <template #cell(moduleSelfDiagnosisStatus)="data">
          <module-self-diagnosis-status-badge
            :value="data.item.moduleSelfDiagnosisStatus"
          />
        </template>
      </s-table>
    </paged-report-panel>
  </app-content>
</template>

<script>
import LoadingMixin from '@/services/loadingMixin';
import NetworkStatusBadge from '@/domain/components/generalPanel/networkStatus/NetworkStatusBadge';
import ModuleStatusBadge from '@/domain/components/generalPanel/networkStatus/ModuleStatusBadge';
import ModuleSelfDiagnosisStatusBadge from '@/domain/components/generalPanel/networkStatus/ModuleSelfDiagnosisStatusBadge';
import storageManager from '@/services/sessionStorageManager';
import PagedReportPanel from '@/domain/components/generalPanel/reports/PagedReportPanel';
import networkStatusService from '@/domain/services/generalPanel/networkStatus';
import { formats } from '@/constants/localizer';
import networkStatusHistoryTableFields
  from '@/domain/data/generalPanel/reports/networkStatus/networkStatusHistoryTableFields';

export default {
  name: 'NetworkStatusReportView',
  components: {
    PagedReportPanel,
    NetworkStatusBadge,
    ModuleStatusBadge,
    ModuleSelfDiagnosisStatusBadge,
  },
  mixins: [LoadingMixin],
  data: () => ({
    formats,
    history: [],
    pagedFilter: {
      pageSize: 15,
      page: 1,
      filter: {},
    },
    sortBy: 'dateTime',
    sortDirection: 'asc',
    totalItems: 0,
  }),
  computed: {
    fields() {
      return networkStatusHistoryTableFields();
    },
  },
  created() {
    this.getStorage();
    this.getItems(true);
  },
  methods: {
    close() {
      this.$confirmMessage({ code: 'MC027' })
        .accepted(() => window.close());
    },
    sorting(sort) {
      this.sortDirection = sort.sortDesc ? 'desc' : 'asc';
      this.sortBy = sort.sortBy;
      this.setCurrentPage(1);
      this.getItems();
    },
    setCurrentPage(page) {
      this.pagedFilter.page = page;
    },
    getItems(showMessage = false) {
      this.startLoading();
      const filters = this.getFilter();
      networkStatusService.getHistoryList(filters)
        .then((response) => {
          this.history = response.data;
          this.totalItems = response.total;
          if (!this.history.length) {
            this.$pushAlert({ code: 'MA009' });
            return;
          }
          if (showMessage) {
            this.$pushAlert({ code: 'MS011' });
          }
        }).finally(() => {
        this.endLoading();
      });
    },
    getFilter() {
      const sort = {
        [this.sortBy]: this.sortDirection,
      };
      return {
        filter: this.pagedFilter.filter,
        page: this.pagedFilter.page,
        pageSize: this.pagedFilter.pageSize,
        sort,
      };
    },
    tryJsonParse(item) {
      try {
        return JSON.parse(item);
      } catch (e) {
        this.$pushAlert({
          message: this.$t('reports.filter-error'),
          type: 'warning',
        });
        return {};
      }
    },
    getStorage() {
      if (!this.$route.query) return;
      const storageItem = storageManager.getItem(this.$route.query.filter);
      this.pagedFilter.filter = this.tryJsonParse(storageItem);
    },
    onRefreshSearch(input) {
      this.pagedFilter = input;
      this.getItems();
    },
  },
};
</script>

<style lang="sass" scoped>
.network-report-view
  &__table
    min-height: 65vh

    ::v-deep
      thead th
        background-color: var(--color-table-header-bg) !important

  &__close-btn-wrapper
    flex: 1
    display: flex
    align-items: flex-end
    justify-content: flex-end

::v-deep
  *:not(.no-align) > table.table:not(.no-align) th:not(:only-child):last-child,
  *:not(.no-align) > table.table:not(.no-align) td:not(:only-child):last-child,
  *:not(.no-align) > table.table:not(.no-align) th:first-child
    text-align: center

  .network-report-view__self-diagnosis-head
    white-space: normal
    position: relative
    max-width: 10rem
</style>
